import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { from } from 'rxjs';
import { Button } from 'primeng/button';
import { InputText } from 'primeng/inputtext';
import { Checkbox } from 'primeng/checkbox';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioFormBase } from '@/shared/components/relatorios/relatorio-form.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { OficioSubstituicaoDiretorTecnicoPjService } from '@/core/api/sicom-api/registro/prestador/relatorios/oficio-substituicao-diretor-tecnico-pj/oficio-substituicao-diretor-tecnico-pj.service';
import {
    ReportOficioSubstituicaoDiretorTecnicoPjRequest,
    ReportOficioSubstituicaoDiretorTecnicoPjResponse
} from '@/core/api/sicom-api/registro/prestador/relatorios/oficio-substituicao-diretor-tecnico-pj/oficio-substituicao-diretor-tecnico-pj.model';
import { OficioSubstituicaoDiretorTecnicoPjJobService } from '@/core/services/oficio-substituicao-diretor-tecnico-pj-job.service';
import { AuthService } from '@/core/auth/auth.service';
import { PessoaService } from '@/shared/services/pessoa.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';

type Req = ReportOficioSubstituicaoDiretorTecnicoPjRequest;
type Resp = ReportOficioSubstituicaoDiretorTecnicoPjResponse;

@Component({
    selector: 'app-oficio-substituicao-diretor-tecnico-pj',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, FormsModule, Button, InputText, Checkbox, RelatorioContainer, CanDirective],
    templateUrl: './oficio-substituicao-diretor-tecnico-pj.html',
    styleUrl: './oficio-substituicao-diretor-tecnico-pj.scss'
})
/**
 * Relatório PDF assíncrono: Ofício de Substituição de Diretor Técnico (prestador PJ).
 * Herda `RelatorioFormBase`: export/polling/download/erro vêm da base; aqui só resolvemos a
 * identificação do prestador (via `PessoaService`, nuCrm + sgUf) e os campos opcionais do ofício
 * (assinante, cargo, número e condição do anexo). O assinante é pré-preenchido com o usuário logado.
 */
export class ReportOficioSubstituicaoDiretorTecnicoPjComponent extends RelatorioFormBase<Req, Resp> {
    /** Gate do botao Gerar: o backend exige esta role em /reports/** do modulo. */
    protected override readonly roleGerar = PERMISSOES.prestadores.relatorios.gerar;

    private readonly oficioService = inject(OficioSubstituicaoDiretorTecnicoPjService);
    private readonly authService = inject(AuthService);
    private readonly pessoaService = inject(PessoaService);
    readonly oficioJobService = inject(OficioSubstituicaoDiretorTecnicoPjJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.oficioJobService.job$;

    assinante = '';
    cargoAssinante = '';
    numeroOficio = '';
    condicaoAnexo = false;

    protected get jobFacade(): RelatorioJobFacade {
        return this.oficioJobService;
    }

    /** Além de restaurar o job (base), pré-preenche o assinante com o nome do usuário logado. */
    override ngOnInit(): void {
        super.ngOnInit();
        this.inicializarUsuarioLogado();
    }

    private inicializarUsuarioLogado(): void {
        from(this.authService.getUserInfo()).subscribe((userInfo) => {
            if (!this.assinante) {
                this.assinante = userInfo.name || '';
            }
        });
    }

    /** Monta o request a partir do prestador selecionado; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const pessoa = this.pessoaService.getPessoa();
        const pessoaReference = this.pessoaService.getPessoaReference();
        const nuCrm = Number(pessoa?.dadosGerais?.crm || pessoa?.crm || pessoa?.registro || 0);
        const sgUf = pessoa?.uf || pessoaReference?.uf || '';

        if (!nuCrm || !sgUf) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: 'Selecione um prestador antes de gerar o ofício.'
            });
            return null;
        }

        return {
            nuCrm,
            sgUf,
            assinante: this.assinante?.trim() || undefined,
            cargoAssinante: this.cargoAssinante?.trim() || undefined,
            numeroOficio: this.numeroOficio?.trim() || undefined,
            condicaoAnexo: this.condicaoAnexo
        };
    }

    protected exportar(request: Req) {
        return this.oficioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.assinante = '';
        this.cargoAssinante = '';
        this.numeroOficio = '';
        this.condicaoAnexo = false;
        this.limparJob();
    }
}
